import * as THREE from 'three';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { Player } from '../entities/Player';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { CollisionSystem } from '../systems/CollisionSystem';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { Hud, type HudPromptTone } from '../systems/Hud';
import { VfxSystem } from '../systems/VfxSystem';
import { createSeededRandom } from '../utils/random';
import {
  createSavannaWorld,
  type FruitVisual,
  type SaplingVisual,
  type ThrowableVisual,
} from '../world';

type ThrownItem = {
  item: ThrowableVisual;
  velocity: THREE.Vector3;
  previousPosition: THREE.Vector3;
};

type FruitReach = {
  fruit: FruitVisual;
  elapsed: number;
  consumed: boolean;
  yaw: number;
};

type InteractionPrompt = {
  label: string | null;
  key: string;
  tone: HudPromptTone;
};

type QuestId = 'restore-saplings' | 'gather-fruit' | 'seed-rings';

type QuestDefinition = {
  id: QuestId;
  target: number;
};

const HARMONY_TARGET = 3;
const QUESTS: readonly QuestDefinition[] = [
  { id: 'restore-saplings', target: HARMONY_TARGET },
  { id: 'gather-fruit', target: 3 },
  { id: 'seed-rings', target: 3 },
];
const INTERACT_RANGE = 2.7;
const SPRAY_RANGE = 10;
const SPRAY_DURATION = 0.56;
const SPRAY_ARC_LIFT = 0.16;
const WATER_PER_SPRAY = 24;
const FRUIT_HEAL = 28;
const FRUIT_REACH_DURATION = 0.9;
const FRUIT_REACH_BITE_TIME = 0.46;
const THORN_DAMAGE = 9;

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 240);
  private readonly hud = new Hud();
  private readonly input: InputController;
  private readonly player = new Player();
  private readonly world = createSavannaWorld();
  private readonly collision = new CollisionSystem();
  private readonly audio = new AudioSystem();
  private readonly vfx = new VfxSystem();
  private readonly cameraRig = new CameraRig(this.camera);
  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    () => this.render(),
  );

  private readonly tuning: DebugTuning = {
    speed: 5.6,
    dashMultiplier: 1.38,
    acceleration: 9.5,
    cameraLag: 0.14,
    exposure: 1.08,
    maxDpr: window.matchMedia('(pointer: coarse)').matches ? 1.35 : 1.75,
  };
  private readonly debugTools: DebugTools;
  private readonly cameraForward = new THREE.Vector3();
  private readonly cameraRight = new THREE.Vector3();
  private readonly facing = new THREE.Vector3();
  private readonly sprayOrigin = new THREE.Vector3();
  private readonly sprayDirection = new THREE.Vector3();
  private readonly sprayEnd = new THREE.Vector3();
  private readonly diagnosticFacing = new THREE.Vector3();
  private readonly diagnosticTrunkOrigin = new THREE.Vector3();
  private readonly diagnosticSprayDirection = new THREE.Vector3();
  private readonly scratchPosition = new THREE.Vector3();
  private readonly throwableHomes = new Map<string, THREE.Vector3>();
  private readonly thrownItems: ThrownItem[] = [];

  private frame = 0;
  private elapsed = 0;
  private harmony = 0;
  private fruitCollected = 0;
  private seedRingsHit = 0;
  private activeQuestIndex = 0;
  private complete = false;
  private completionAcknowledged = false;
  private paused = true;
  private carriedItem: ThrowableVisual | null = null;
  private fruitReach: FruitReach | null = null;
  private sprayTimer = 0;
  private thornCooldown = 0;
  private hitstopRemaining = 0;
  private prompt: InteractionPrompt = { label: null, key: 'E', tone: 'neutral' };
  private rng = createSeededRandom(1);
  private pausedForScreenshot = false;
  private reducedMotion = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;
    const touch = this.hud.getTouchElements();
    this.input = new InputController(touch.stick, touch.knob, touch.primaryButton, canvas);
    this.audio.setRandomSource(() => this.rng());
    this.debugTools = new DebugTools(this.tuning, () => {
      this.renderer.toneMappingExposure = this.tuning.exposure;
      resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    });

    for (const item of this.world.throwables) {
      this.throwableHomes.set(item.id, item.position.clone());
    }

    this.createScene();
    this.bindHud();
    this.resetRun(true);
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.installTestHooks();
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.hud.dispose();
    this.vfx.dispose();
    this.player.dispose();
    this.world.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private update(realDelta: number, elapsed: number): void {
    this.frame += 1;
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }

    if (this.input.consumePause() && !this.player.collapsed && !this.complete) {
      this.paused = !this.paused;
      this.hud.showIntro(false);
      this.hud.setPaused(this.paused);
    }
    if (this.input.consumeRestart()) this.resetRun();
    if (this.hitstopRemaining > 0) this.hitstopRemaining = Math.max(0, this.hitstopRemaining - realDelta);
    this.sprayTimer = Math.max(0, this.sprayTimer - realDelta);
    const gameplayDelta = this.hitstopRemaining > 0 ? realDelta * 0.06 : realDelta;
    const animationDelta = this.reducedMotion ? 0 : realDelta;
    const animationElapsed = this.reducedMotion ? 0 : elapsed;

    if (!this.paused) {
      if (!this.complete && !this.player.collapsed) this.elapsed += gameplayDelta;
      this.cameraRig.getForward(this.cameraForward);
      this.cameraRig.getRight(this.cameraRight);
      this.updateActions();
      this.updateFruitReach(gameplayDelta);
      this.updateTrunkState();
      this.player.update(
        gameplayDelta,
        animationElapsed,
        this.input,
        this.tuning,
        this.world.bounds,
        this.cameraForward,
        this.cameraRight,
        animationDelta,
      );
      this.player.group.position.y = this.world.sampleGroundHeight(
        this.player.group.position.x,
        this.player.group.position.z,
      );
      this.emitActiveSpray(animationDelta);
      this.world.update(animationDelta, animationElapsed);
      this.updateThrownItems(gameplayDelta);
      this.updatePressure(gameplayDelta);
    } else {
      this.world.update(animationDelta, animationElapsed);
    }

    this.vfx.update(animationDelta);
    this.cameraRig.update(realDelta, this.player.group.position, this.tuning.cameraLag, this.input);
    this.prompt = this.resolvePrompt();
    this.updateHud();
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private createScene(): void {
    this.scene.background = new THREE.Color('#d9aa69');
    this.scene.fog = new THREE.FogExp2('#d8aa70', 0.0065);

    const hemisphere = new THREE.HemisphereLight('#fff1c7', '#725331', 2.15);
    this.scene.add(hemisphere);
    const sun = new THREE.DirectionalLight('#ffe3a6', 3.4);
    sun.position.set(-38, 62, 24);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 155;
    sun.shadow.camera.left = -62;
    sun.shadow.camera.right = 62;
    sun.shadow.camera.top = 62;
    sun.shadow.camera.bottom = -62;
    sun.shadow.bias = -0.00025;
    this.scene.add(sun);

    this.scene.add(this.world.root, this.player.group, this.vfx.root);
  }

  private bindHud(): void {
    this.hud.bindActions({
      onPause: () => {
        this.paused = true;
      },
      onResume: () => {
        this.paused = false;
      },
      onRestart: () => this.resetRun(),
      onContinue: () => {
        this.completionAcknowledged = true;
      },
      onIntroDismiss: () => {
        this.paused = false;
      },
      onTouchAction: (action, active) => {
        if (!active) return;
        if (action === 'interact') this.input.queueInteract();
        else this.input.queuePrimaryAction();
      },
    });
  }

  private updateActions(): void {
    if (this.player.collapsed) return;
    if (this.input.consumeInteract()) this.performInteraction();
    if (this.input.consumePrimaryAction()) this.performPrimaryAction();
  }

  private performInteraction(): void {
    if (this.fruitReach) return;
    if (this.carriedItem) {
      this.dropCarriedItem();
      return;
    }

    if (this.isNearWater()) {
      const filled = this.player.fillWater(this.player.maxTrunkWater);
      if (filled > 0) {
        this.audio.play('fill');
        this.hud.flashWater('fill');
        this.vfx.emitInteraction(this.world.waterhole.position, 'water-impact');
        this.cameraRig.addTrauma(0.12);
      }
      return;
    }

    const fruit = this.findNearestFruit();
    if (fruit) {
      this.startFruitReach(fruit);
      return;
    }

    const throwable = this.findNearestThrowable();
    if (throwable) this.pickUp(throwable);
  }

  private performPrimaryAction(): void {
    if (this.carriedItem) {
      this.throwCarriedItem();
      return;
    }
    if (this.player.trunkWater <= 0) {
      this.audio.play('empty');
      this.hud.flashWater('empty');
      return;
    }

    this.player.consumeWater(WATER_PER_SPRAY);
    this.sprayTimer = SPRAY_DURATION;
    this.audio.play('spray');
    this.hud.flashWater('spray');
    this.cameraRig.addTrauma(0.09);
    this.cameraRig.punchFov(1.2);
    this.getPlayerFacing(this.facing);
    this.sprayOrigin.copy(this.player.group.position).addScaledVector(this.facing, 2.4);
    this.sprayOrigin.y += 2.1;
    this.sprayEnd.copy(this.sprayOrigin).addScaledVector(this.facing, SPRAY_RANGE);
    const sapling = this.findAimedSapling(this.sprayOrigin, this.sprayEnd);
    if (sapling) {
      sapling.setRevived(true);
      this.harmony = this.world.saplings.filter((entry) => entry.revived).length;
      this.audio.play('bloom');
      this.vfx.emitInteraction(sapling.position, 'revive');
      this.cameraRig.addTrauma(0.22);
      this.advanceCompletedQuests();
    } else {
      this.scratchPosition.copy(this.sprayEnd);
      this.scratchPosition.y = this.world.sampleGroundHeight(this.scratchPosition.x, this.scratchPosition.z) + 0.08;
      this.vfx.emitInteraction(this.scratchPosition, 'water-impact');
    }
  }

  private startFruitReach(fruit: FruitVisual): void {
    this.player.group.updateWorldMatrix(true, false);
    this.scratchPosition.copy(fruit.position);
    this.player.group.worldToLocal(this.scratchPosition);
    const horizontalDistanceSq = this.scratchPosition.x ** 2 + this.scratchPosition.z ** 2;
    const yaw = horizontalDistanceSq > 0.001
      ? Math.atan2(-this.scratchPosition.x, -this.scratchPosition.z)
      : 0;
    this.fruitReach = {
      fruit,
      elapsed: 0,
      consumed: false,
      yaw: THREE.MathUtils.clamp(yaw, -1.15, 1.15),
    };
  }

  private updateFruitReach(delta: number): void {
    const reach = this.fruitReach;
    if (!reach) {
      this.player.trunkReach = 0;
      this.player.trunkYaw = 0;
      return;
    }

    reach.elapsed += delta;
    const progress = THREE.MathUtils.clamp(reach.elapsed / FRUIT_REACH_DURATION, 0, 1);
    this.player.trunkReach = Math.sin(progress * Math.PI);
    this.player.trunkYaw = reach.yaw;

    if (!reach.consumed && reach.elapsed >= FRUIT_REACH_BITE_TIME) {
      reach.consumed = true;
      reach.fruit.setConsumed(true);
      this.fruitCollected += 1;
      this.player.restoreHealth(FRUIT_HEAL);
      this.audio.play('eat');
      this.hud.flashHealth('restore');
      this.hud.flashPickup('Sweet acacia fruit restored vitality');
      this.vfx.emitInteraction(reach.fruit.position, 'eat');
      this.advanceCompletedQuests();
    }

    if (progress >= 1) {
      this.fruitReach = null;
      this.player.trunkReach = 0;
      this.player.trunkYaw = 0;
    }
  }

  private updateTrunkState(): void {
    this.player.spraying = this.sprayTimer > 0;
    this.player.carrying = this.carriedItem !== null;
    if (this.fruitReach) {
      this.player.trunkLift = 0;
      this.player.trunkCurl = 0;
    } else if (this.player.spraying) {
      this.player.trunkLift = 1;
      this.player.trunkCurl = 0.35;
    } else if (this.player.carrying) {
      this.player.trunkLift = 0.72;
      this.player.trunkCurl = 0.25;
    } else {
      this.player.trunkLift = 0;
      this.player.trunkCurl = 0;
    }
  }

  private emitActiveSpray(delta: number): void {
    if (!this.player.spraying || delta <= 0) return;
    this.player.spraySocket.getWorldPosition(this.sprayOrigin);
    this.getSprayDirection(this.sprayDirection);
    this.vfx.emitSpray(this.sprayOrigin, this.sprayDirection, 1.15, delta);
  }

  private pickUp(item: ThrowableVisual): void {
    const thrownIndex = this.thrownItems.findIndex((entry) => entry.item === item);
    if (thrownIndex >= 0) this.thrownItems.splice(thrownIndex, 1);
    this.carriedItem = item;
    item.setHeld(true);
    this.player.carrySocket.add(item.root);
    item.root.position.set(0, 0, 0);
    item.root.rotation.set(0, 0, 0);
    this.player.carrying = true;
    this.audio.play('pickup');
    this.vfx.emitInteraction(this.player.group.position, 'pickup');
    this.hud.flashPickup(`Picked up ${this.itemLabel(item)}`);
  }

  private dropCarriedItem(): void {
    const item = this.carriedItem;
    if (!item) return;
    this.world.root.attach(item.root);
    item.root.position.y = this.world.sampleGroundHeight(item.root.position.x, item.root.position.z) + 0.2;
    item.setHeld(false);
    this.carriedItem = null;
    this.player.carrying = false;
  }

  private throwCarriedItem(): void {
    const item = this.carriedItem;
    if (!item) return;
    this.world.root.attach(item.root);
    item.setHeld(false);
    const throwGround = this.world.sampleGroundHeight(item.root.position.x, item.root.position.z);
    item.root.position.y = Math.max(item.root.position.y, throwGround + 1.45);
    this.getPlayerFacing(this.facing);
    const velocity = this.facing.clone().multiplyScalar(11.5);
    velocity.y = 5.2;
    this.thrownItems.push({
      item,
      velocity,
      previousPosition: item.root.position.clone(),
    });
    this.carriedItem = null;
    this.player.carrying = false;
    this.audio.play('throw');
    this.vfx.emitInteraction(item.root.position, 'pickup');
    this.cameraRig.addTrauma(0.18);
    this.cameraRig.punchFov(2.5);
  }

  private updateThrownItems(delta: number): void {
    for (let index = this.thrownItems.length - 1; index >= 0; index -= 1) {
      const thrown = this.thrownItems[index];
      thrown.previousPosition.copy(thrown.item.root.position);
      thrown.velocity.y -= 10.8 * delta;
      thrown.item.root.position.addScaledVector(thrown.velocity, delta);
      thrown.item.root.rotation.x += delta * 5.4;
      thrown.item.root.rotation.z += delta * 3.2;

      for (const target of this.world.targetRings) {
        if (target.hit || thrown.item.kind !== 'seed-pod') continue;
        this.scratchPosition.copy(target.position);
        this.scratchPosition.y += 1.85;
        if (
          this.collision.segmentHitsCircleXZ(
            thrown.previousPosition,
            thrown.item.root.position,
            this.scratchPosition,
            target.radius,
          ) &&
          Math.abs(thrown.item.root.position.y - this.scratchPosition.y) < 2
        ) {
          target.setHit(true);
          this.seedRingsHit = this.world.targetRings.filter((entry) => entry.hit).length;
          this.audio.play('bloom');
          this.vfx.emitInteraction(this.scratchPosition, 'throw-impact');
          this.cameraRig.addTrauma(0.28);
          this.advanceCompletedQuests();
        }
      }

      const ground = this.world.sampleGroundHeight(
        thrown.item.root.position.x,
        thrown.item.root.position.z,
      );
      if (thrown.item.root.position.y <= ground + 0.18) {
        thrown.item.root.position.y = ground + 0.18;
        this.vfx.emitInteraction(thrown.item.root.position, 'throw-impact');
        this.audio.play('throw', 0.55);
        this.thrownItems.splice(index, 1);
      }
    }
  }

  private updatePressure(delta: number): void {
    this.thornCooldown = Math.max(0, this.thornCooldown - delta);
    const shaded = this.isInShade();
    const dry = this.player.trunkWater <= 0.01;
    const heatRate = shaded ? -0.24 : dry ? 0.025 : -0.055;
    this.player.setHeat(this.player.heat + heatRate * delta);
    if (this.player.heat > 0.62) this.damagePlayer((this.player.heat - 0.55) * 2.2 * delta, false);

    if (this.thornCooldown <= 0) {
      const hazard = this.world.hazards.find((entry) =>
        this.collision.overlapsXZ(
          this.player.group.position,
          this.player.radius * 0.7,
          entry.position,
          entry.radius,
        ),
      );
      if (hazard) {
        this.thornCooldown = 1.15;
        this.player.velocity.multiplyScalar(-0.25);
        this.damagePlayer(THORN_DAMAGE, true);
        this.vfx.emitInteraction(this.player.group.position, 'thorn-hit');
      }
    }
  }

  private damagePlayer(amount: number, heavy: boolean): void {
    const collapsedNow = this.player.applyDamage(amount);
    if (heavy) {
      this.audio.play('damage');
      this.hud.flashHealth('damage');
      this.cameraRig.addTrauma(0.42);
      this.hitstopRemaining = Math.max(this.hitstopRemaining, 0.07);
    }
    if (collapsedNow) {
      this.audio.play('collapse');
      this.cameraRig.addTrauma(0.7);
      this.carriedItem && this.dropCarriedItem();
    }
  }

  private resolvePrompt(): InteractionPrompt {
    if (this.paused) return { label: null, key: 'E', tone: 'neutral' };
    if (this.player.collapsed) return { label: 'Rest beneath the acacia and try again', key: 'R', tone: 'danger' };
    if (this.fruitReach) return { label: 'Reaching for fruit...', key: 'E', tone: 'food' };
    if (this.carriedItem) {
      return { label: `Drop ${this.itemLabel(this.carriedItem)} · Space throws`, key: 'E', tone: 'neutral' };
    }
    if (this.isNearWater()) {
      return {
        label: this.player.trunkWater >= this.player.maxTrunkWater ? 'Trunk is full' : 'Fill trunk with cool water',
        key: 'E',
        tone: 'water',
      };
    }
    const fruit = this.findNearestFruit();
    if (fruit) return { label: 'Reach for fruit and eat', key: 'E', tone: 'food' };
    const item = this.findNearestThrowable();
    if (item) return { label: `Pick up ${this.itemLabel(item)}`, key: 'E', tone: 'neutral' };
    const sapling = this.findAimedSaplingFromPlayer();
    if (sapling) {
      return {
        label: this.player.trunkWater > 0 ? 'Spray the dry sapling' : 'Find water before spraying',
        key: 'Space',
        tone: this.player.trunkWater > 0 ? 'water' : 'danger',
      };
    }
    return { label: null, key: 'E', tone: 'neutral' };
  }

  private updateHud(): void {
    const completionVisible = this.complete && !this.completionAcknowledged;
    const activeQuest = this.getActiveQuest();
    const questTarget = activeQuest?.target ?? QUESTS.at(-1)?.target ?? 0;
    const questProgress = activeQuest ? this.getQuestProgress(activeQuest) : questTarget;
    this.hud.updateGameState({
      health: this.player.health,
      maxHealth: this.player.maxHealth,
      water: this.player.trunkWater,
      maxWater: this.player.maxTrunkWater,
      harmony: questProgress,
      harmonyTarget: questTarget,
      objective: this.complete ? 'All savanna quests complete' : this.getQuestObjective(activeQuest),
      questStage: Math.min(this.activeQuestIndex + 1, QUESTS.length),
      questCount: QUESTS.length,
      prompt: this.prompt.label,
      promptKey: this.prompt.key,
      promptTone: this.prompt.tone,
      heldItem: this.carriedItem ? this.itemLabel(this.carriedItem) : null,
      paused: this.paused,
      collapsed: this.player.collapsed,
      complete: completionVisible,
      heatWarning: this.player.heat > 0.58,
    });
  }

  private getActiveQuest(): QuestDefinition | null {
    return QUESTS[this.activeQuestIndex] ?? null;
  }

  private getQuestProgress(quest: QuestDefinition): number {
    switch (quest.id) {
      case 'restore-saplings':
        return this.harmony;
      case 'gather-fruit':
        return this.fruitCollected;
      case 'seed-rings':
        return this.seedRingsHit;
    }
  }

  private getQuestObjective(quest: QuestDefinition | null): string {
    if (!quest) return 'All savanna quests complete';
    const remaining = Math.max(0, quest.target - this.getQuestProgress(quest));
    switch (quest.id) {
      case 'restore-saplings':
        return `Revive ${remaining} dry sapling${remaining === 1 ? '' : 's'}`;
      case 'gather-fruit':
        return `Eat ${remaining} fruit${remaining === 1 ? '' : 's'} from the acacias`;
      case 'seed-rings':
        return `Throw seed pods through ${remaining} ring${remaining === 1 ? '' : 's'}`;
    }
  }

  private advanceCompletedQuests(): void {
    const previousQuestIndex = this.activeQuestIndex;
    let activeQuest = this.getActiveQuest();
    while (activeQuest && this.getQuestProgress(activeQuest) >= activeQuest.target) {
      this.activeQuestIndex += 1;
      activeQuest = this.getActiveQuest();
    }
    if (this.activeQuestIndex === previousQuestIndex) return;

    this.hud.flashQuest();
    this.cameraRig.addTrauma(0.14);
    if (!activeQuest) {
      this.complete = true;
      this.completionAcknowledged = false;
    }
  }

  private isNearWater(): boolean {
    return (
      this.collision.distanceSqXZ(this.player.group.position, this.world.waterhole.position) <=
      this.world.waterhole.fillRadius * this.world.waterhole.fillRadius
    );
  }

  private isInShade(): boolean {
    return this.world.landmarks.some(
      (landmark) =>
        (landmark.id === 'central-acacia' || landmark.id === 'western-baobab') &&
        this.collision.distanceSqXZ(this.player.group.position, landmark.position) <=
          landmark.radius * landmark.radius,
    );
  }

  private findNearestFruit(): FruitVisual | null {
    let nearest: FruitVisual | null = null;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;
    for (const fruit of this.world.fruits) {
      if (fruit.consumed) continue;
      const distanceSq = this.collision.distanceSqXZ(this.player.group.position, fruit.position);
      const range = INTERACT_RANGE + fruit.radius;
      if (distanceSq <= range * range && distanceSq < nearestDistanceSq) {
        nearest = fruit;
        nearestDistanceSq = distanceSq;
      }
    }
    return nearest;
  }

  private findNearestThrowable(): ThrowableVisual | null {
    let nearest: ThrowableVisual | null = null;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;
    for (const item of this.world.throwables) {
      if (item.held) continue;
      item.root.getWorldPosition(this.scratchPosition);
      const distanceSq = this.collision.distanceSqXZ(this.player.group.position, this.scratchPosition);
      const range = INTERACT_RANGE + item.radius;
      if (distanceSq <= range * range && distanceSq < nearestDistanceSq) {
        nearest = item;
        nearestDistanceSq = distanceSq;
      }
    }
    return nearest;
  }

  private findAimedSaplingFromPlayer(): SaplingVisual | null {
    this.sprayOrigin.copy(this.player.group.position);
    this.sprayOrigin.y += 2.1;
    this.getPlayerFacing(this.facing);
    this.sprayEnd.copy(this.sprayOrigin).addScaledVector(this.facing, SPRAY_RANGE);
    return this.findAimedSapling(this.sprayOrigin, this.sprayEnd);
  }

  private findAimedSapling(start: THREE.Vector3, end: THREE.Vector3): SaplingVisual | null {
    let best: SaplingVisual | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const sapling of this.world.saplings) {
      if (sapling.revived) continue;
      if (!this.collision.segmentHitsCircleXZ(start, end, sapling.position, sapling.radius + 0.7)) continue;
      const distance = this.collision.distanceSqXZ(start, sapling.position);
      if (distance < bestDistance) {
        best = sapling;
        bestDistance = distance;
      }
    }
    return best;
  }

  private getPlayerFacing(target: THREE.Vector3): THREE.Vector3 {
    return target.set(0, 0, -1).applyQuaternion(this.player.group.quaternion).setY(0).normalize();
  }

  private getSprayDirection(target: THREE.Vector3): THREE.Vector3 {
    this.getPlayerFacing(target);
    target.y = SPRAY_ARC_LIFT;
    return target.normalize();
  }

  private itemLabel(item: ThrowableVisual): string {
    if (item.kind === 'seed-pod') return 'seed pod';
    return item.kind;
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
        this.audio.setRandomSource(() => this.rng());
      },
      setState: (name: string) => this.setTestState(name),
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.cameraRig.setReducedMotion(enabled);
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
      teleport: (x: number, z: number) => this.teleport(x, z),
      setVitals: (health: number, water: number, heat = this.player.heat) => {
        this.player.health = THREE.MathUtils.clamp(health, 0, this.player.maxHealth);
        this.player.trunkWater = THREE.MathUtils.clamp(water, 0, this.player.maxTrunkWater);
        this.player.setHeat(heat);
        this.player.collapsed = this.player.health <= 0;
      },
      interact: () => this.input.queueInteract(),
      primaryAction: () => this.input.queuePrimaryAction(),
      restart: () => this.input.queueRestart(),
    };
  }

  private setTestState(name: string): void {
    this.resetRun();
    switch (name) {
      case 'active-play':
        break;
      case 'water-ready':
        this.teleport(this.world.waterhole.position.x - 9, this.world.waterhole.position.z);
        this.player.fillWater(this.player.maxTrunkWater);
        break;
      case 'carrying':
        this.teleport(
          this.world.throwables[0].position.x,
          this.world.throwables[0].position.z + 1.8,
        );
        this.pickUp(this.world.throwables[0]);
        break;
      case 'sapling-quest-ready':
        this.world.saplings[0]?.setRevived(true);
        this.world.saplings[1]?.setRevived(true);
        this.harmony = 2;
        this.player.fillWater(this.player.maxTrunkWater);
        this.teleport(-5, 46);
        break;
      case 'fruit-quest-ready':
        for (const sapling of this.world.saplings) sapling.setRevived(true);
        this.harmony = HARMONY_TARGET;
        this.activeQuestIndex = 1;
        this.world.fruits[0]?.setConsumed(true);
        this.world.fruits[1]?.setConsumed(true);
        this.fruitCollected = 2;
        this.player.health = 50;
        this.teleport(2.5, 3.6);
        break;
      case 'ring-quest-ready': {
        for (const sapling of this.world.saplings) sapling.setRevived(true);
        this.harmony = HARMONY_TARGET;
        for (const fruit of this.world.fruits.slice(0, 3)) fruit.setConsumed(true);
        this.fruitCollected = 3;
        this.activeQuestIndex = 2;
        this.world.targetRings[0]?.setHit(true);
        this.world.targetRings[1]?.setHit(true);
        this.seedRingsHit = 2;
        const seedPod = this.world.throwables.find((item) => item.kind === 'seed-pod');
        if (seedPod) this.pickUp(seedPod);
        this.teleport(-26, 29);
        this.player.group.rotation.y = 0;
        break;
      }
      case 'low-health':
        this.player.health = 18;
        this.player.setHeat(0.78);
        break;
      case 'collapsed':
        this.player.health = 0;
        this.player.collapsed = true;
        break;
      case 'complete':
        for (const sapling of this.world.saplings) sapling.setRevived(true);
        this.harmony = HARMONY_TARGET;
        for (const fruit of this.world.fruits.slice(0, 3)) fruit.setConsumed(true);
        this.fruitCollected = 3;
        for (const target of this.world.targetRings) target.setHit(true);
        this.seedRingsHit = 3;
        this.activeQuestIndex = QUESTS.length;
        this.complete = true;
        break;
      default:
        console.warn(`Unknown test state: ${name}`);
    }
    this.updateHud();
  }

  private resetRun(showIntro = false): void {
    this.fruitReach = null;
    if (this.carriedItem) {
      this.world.root.attach(this.carriedItem.root);
      this.carriedItem = null;
    }
    this.world.reset();
    for (const item of this.world.throwables) {
      this.world.root.attach(item.root);
      item.root.position.copy(this.throwableHomes.get(item.id) ?? item.position);
      item.root.rotation.set(0, 0, 0);
      item.setHeld(false);
    }
    this.thrownItems.length = 0;
    const startX = 4.5;
    const startZ = 7.5;
    this.player.reset(new THREE.Vector3(startX, this.world.sampleGroundHeight(startX, startZ), startZ));
    this.vfx.reset();
    this.elapsed = 0;
    this.harmony = 0;
    this.fruitCollected = 0;
    this.seedRingsHit = 0;
    this.activeQuestIndex = 0;
    this.complete = false;
    this.completionAcknowledged = false;
    this.thornCooldown = 0;
    this.hitstopRemaining = 0;
    this.sprayTimer = 0;
    this.player.spraying = false;
    this.player.carrying = false;
    this.player.trunkLift = 0;
    this.player.trunkCurl = 0;
    this.player.trunkReach = 0;
    this.player.trunkYaw = 0;
    this.paused = showIntro;
    this.hud.showIntro(showIntro);
    this.hud.showCollapse(false);
    this.hud.showCompletion(false);
    this.cameraRig.snapTo(this.player.group.position);
    if (!showIntro) this.audio.play('restart');
    this.updateHud();
  }

  private teleport(x: number, z: number): void {
    this.player.group.position.set(x, this.world.sampleGroundHeight(x, z), z);
    this.player.velocity.set(0, 0, 0);
    this.cameraRig.snapTo(this.player.group.position);
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    const activeQuest = this.getActiveQuest();
    this.getPlayerFacing(this.diagnosticFacing);
    this.player.spraySocket.getWorldPosition(this.diagnosticTrunkOrigin);
    this.getSprayDirection(this.diagnosticSprayDirection);
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: this.harmony,
      targetScore: HARMONY_TARGET,
      complete: this.complete,
      paused: this.paused,
      objective: {
        harmony: this.harmony,
        target: HARMONY_TARGET,
        remainingSaplings: this.world.saplings.filter((sapling) => !sapling.revived).length,
        questId: activeQuest?.id ?? null,
        questStage: Math.min(this.activeQuestIndex + 1, QUESTS.length),
        questCount: QUESTS.length,
        questProgress: activeQuest
          ? this.getQuestProgress(activeQuest)
          : QUESTS.at(-1)?.target ?? 0,
        questTarget: activeQuest?.target ?? QUESTS.at(-1)?.target ?? 0,
        fruitCollected: this.fruitCollected,
        seedRingsHit: this.seedRingsHit,
      },
      player: {
        position: {
          x: this.player.group.position.x,
          y: this.player.group.position.y,
          z: this.player.group.position.z,
        },
        velocity: {
          x: this.player.velocity.x,
          y: this.player.velocity.y,
          z: this.player.velocity.z,
        },
        facing: {
          x: this.diagnosticFacing.x,
          y: this.diagnosticFacing.y,
          z: this.diagnosticFacing.z,
        },
        speed: this.player.velocity.length(),
        health: this.player.health,
        water: this.player.trunkWater,
        heat: this.player.heat,
        collapsed: this.player.collapsed,
        heldItem: this.carriedItem?.id ?? null,
        visual: this.player.getVisualDiagnostics(),
        trunk: {
          origin: {
            x: this.diagnosticTrunkOrigin.x,
            y: this.diagnosticTrunkOrigin.y,
            z: this.diagnosticTrunkOrigin.z,
          },
          sprayDirection: {
            x: this.diagnosticSprayDirection.x,
            y: this.diagnosticSprayDirection.y,
            z: this.diagnosticSprayDirection.z,
          },
          spraying: this.player.spraying,
          reaching: this.fruitReach !== null,
          reachAmount: this.player.trunkReach,
        },
      },
      camera: this.cameraRig.diagnostics(),
      input: this.input.snapshot(),
      interactions: {
        prompt: this.prompt.label,
        fruitsRemaining: this.world.fruits.filter((fruit) => !fruit.consumed).length,
        activeThrows: this.thrownItems.length,
        targetsHit: this.world.targetRings.filter((target) => target.hit).length,
        inWaterRange: this.isNearWater(),
      },
      world: this.world.diagnostics(),
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }
}
